/**
 * One event card (the prototype's card A), drawn by the events row and the
 * events grid: the photo, the date line with Book at its right end, the name,
 * the short line, and "In person · 3 spots left · $60". The whole card opens
 * the event. Dates and times are in the visitor's time zone.
 *
 * With the mine option, an event the client has booked (web-studio-sites 6.6)
 * shows its mark in the spots' place and the mark's button in Book's place,
 * opening the booking. The card then holds two links, so the name's link
 * stretches over the card instead of the card being one link.
 */
import type { ReactNode } from 'react';
import { LOCATION_KIND_LABEL, type SiteEventSummaryDto } from '@telify/shared';

import { partsOf } from '../../contract/runtime';
import { onLink } from '../../ui/onLink';
import { formatSlotTime, priceText } from '../../format';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { Photo } from '../../ui/Photo';
import css from './EventCard.module.css';
import { eventCardParts, type MineMark } from './contract';

/** The card's parts are the same in the events row and the events grid. */
const part = partsOf({ parts: eventCardParts });

function datePart(iso: string, tz: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: tz }).format(new Date(iso));
}

export function spotsText(spotsLeft: number): string {
  if (spotsLeft <= 0) return 'Full';
  return spotsLeft === 1 ? '1 spot left' : `${spotsLeft} spots left`;
}

export interface EventCardProps {
  readonly event: SiteEventSummaryDto;
  readonly timezone: string;
  /** Draw the photo frame (empty when this event has no photo, so the row's date lines stay level). */
  readonly showPhoto: boolean;
  readonly showSummary: boolean;
  /** The client's own booking of the event, with the mine option. */
  readonly mark?: MineMark;
  readonly onOpen: () => void;
  readonly onOpenBooking: () => void;
  readonly className?: string;
}

export function EventCard({ event, timezone, showPhoto, showSummary, mark, onOpen, onOpenBooking, className }: EventCardProps) {
  const free = event.price.kind === 'FREE';
  const body: ReactNode = (
    <>
      {showPhoto && (
        <div className={css.evImg} {...part('photo')}>
          {event.photo && <Photo photo={event.photo} loading="lazy" {...part('photo-image')} />}
        </div>
      )}
      <div className={css.evBody} {...part('body')}>
        <div className={css.evDate} {...part('date')}>
          <span className={css.evDay} {...part('day')}>
            {datePart(event.startTime, timezone, { day: 'numeric' })}
          </span>
          <span className={css.evWhen} {...part('when')}>
            <span className={css.evMon} {...part('month')}>
              {datePart(event.startTime, timezone, { month: 'long' })}
            </span>
            <span className={css.evTime} {...part('time')}>
              {datePart(event.startTime, timezone, { weekday: 'short' })} · {formatSlotTime(event.startTime, timezone)}
            </span>
          </span>
          {mark ? (
            <a className={`${css.evBook} ${css.evMineAction}`} href={mark.action.href} onClick={onLink(onOpenBooking)} {...part('mine-action', mark.state)}>
              {mark.action.label}
            </a>
          ) : (
            <span className={`${css.evBook} ${event.isBookable ? '' : css.evBookOff}`} {...part('book', !event.isBookable && 'unavailable')}>
              Book
            </span>
          )}
        </div>
        <h3 className={`${shared.h3} ${css.evName}`} {...part('name')}>
          {mark ? (
            <a className={css.stretch} href={event.path} onClick={onLink(onOpen)}>
              {event.title}
            </a>
          ) : (
            event.title
          )}
        </h3>
        {showSummary && event.summary && (
          <p className={css.evSum} {...part('summary')}>
            {event.summary}
          </p>
        )}
        <div className={css.evMeta} {...part('meta')}>
          <span {...part('format')}>{LOCATION_KIND_LABEL[event.modality]}</span>
          {/* A booking whose state could not be read shows only its button, never a guessed line. */}
          {mark ? (
            mark.label && (
              <>
                <span className={css.sep} aria-hidden="true" {...part('separator')}>
                  ·
                </span>
                <span className={css.mine} {...part('mine', mark.state)}>
                  {mark.state === 'going' && <Icon name="check" small />}
                  {mark.label}
                </span>
              </>
            )
          ) : (
            <>
              <span className={css.sep} aria-hidden="true" {...part('separator')}>
                ·
              </span>
              <span className={event.spotsLeft <= 3 ? css.low : undefined} {...part('spots', event.spotsLeft <= 0 ? 'full' : event.spotsLeft <= 3 && 'low')}>
                {spotsText(event.spotsLeft)}
              </span>
            </>
          )}
          <span className={css.sep} aria-hidden="true" {...part('separator')}>
            ·
          </span>
          <b className={free ? css.free : undefined} {...part('price')}>
            {priceText(event.price)}
          </b>
        </div>
      </div>
    </>
  );
  return (
    <li className={className} {...part('item')}>
      {mark ? (
        <div className={`${css.ev} ${css.evMarked}`} {...part('card')}>
          {body}
        </div>
      ) : (
        <a className={css.ev} href={event.path} onClick={onLink(onOpen)} {...part('card')}>
          {body}
        </a>
      )}
    </li>
  );
}
